import { useEffect } from 'react';
import { gameServer } from './client';

// When a screen breaks in class, it tells the game server what broke (POST /oops), which writes it to the Worker's
// log. Nov 2 2026: the projector went white in a Lantern Line lesson and there was nothing anywhere to read.
// What goes: where (which screen), the error and its stack, the page and the room's code. Nothing about the student.
// A page sends at most a handful, and the same error only once every half minute.

let room: string | null = null;
let sent = 0;
const lately = new Map<string, number>();
const MAX_A_PAGE = 12;

export function reportOops(where: string, error: unknown) {
  try {
    const e = error instanceof Error ? error : null;
    const msg = e ? `${e.name}: ${e.message}` : String(error);
    const now = Date.now();
    if (sent >= MAX_A_PAGE || now - (lately.get(where + msg) ?? -Infinity) < 30_000) return;
    lately.set(where + msg, now);
    sent++;
    const body = JSON.stringify({ where, msg, stack: e?.stack, path: location.pathname, room });
    void gameServer().then((base) => {
      if (base) void fetch(`${base}/oops`, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
    });
  } catch {
    /* reporting must never break anything itself */
  }
}

// A live game page (the projector, the control laptop, a student laptop) reports what React doesn't catch too: an
// error in a 3D world's frame, a promise nobody waited on. `code` is the room the page is in.
export function useOopsReports(where: string, code: string | null | undefined) {
  useEffect(() => {
    room = code ?? null;
  }, [code]);
  useEffect(() => {
    const onError = (e: ErrorEvent) => reportOops(`${where} (uncaught)`, e.error ?? e.message);
    // (not the browser holding sound back until a tap, or a fetch called off: those are everyday)
    const onRejection = (e: PromiseRejectionEvent) => {
      const name = (e.reason as { name?: string } | null)?.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') reportOops(`${where} (promise)`, e.reason);
    };
    addEventListener('error', onError);
    addEventListener('unhandledrejection', onRejection);
    return () => {
      removeEventListener('error', onError);
      removeEventListener('unhandledrejection', onRejection);
    };
  }, [where]);
}
